import {type ThunkResult} from '../../types/redux';
import * as ViewActions from '../views/actions';
import {makeViewListSelector} from '../views/selectors';
import * as ViewThunks from '../views/thunks';
import type * as ViewTypes from '../views/types';
import {initStart, newWorkspaceViews} from './actions';
import {loadNamedWorkspaceForUrl} from './namedWorkspacesActions';
import {
  getNamedWorkspaceIdForView,
  viewIsLegacyWorkspace,
  viewIsNamedWorkspace,
} from './names';
import {loadPerms} from './nwPermissions';
import type * as Types from './types';
import {type WorkspaceUrl} from './urlTypes';

/* This is called when the user first visits a workspace URL.
 * Responsible for doing initial set up of the redux state and should only
 * be called once.
 */
export function init(
  workspaceStateId: string,
  entityName: string,
  projectName: string,
  viewType: Types.ViewTypes,
  workspaceUrl: WorkspaceUrl,
  defaultSpec: Types.WorkspaceMaterialized,
  relatedId: Types.WsRelatedId
): ThunkResult<Promise<string | void>> {
  return async (dispatch, getState, client) => {
    // Create the loadMetadatalist start action so we can get it's ID.
    const startAction = ViewActions.loadMetadataListStarted({
      entityName,
      projectName,
      viewType,
    });

    // Setup the view first
    dispatch(startAction);
    // Then the workspace redux state
    dispatch(
      initStart(
        workspaceStateId,
        viewType,
        startAction.payload.id,
        defaultSpec,
        relatedId,
        entityName,
        projectName
      )
    );

    // Kick off permission loading now so it overlaps the metadata fetch below
    // rather than waiting for it. loadNamedWorkspaceForUrl awaits this promise
    // and surfaces any error there, so the error behavior is unchanged.
    const viewer = getState().viewer.viewer;
    const permsPromise = loadPerms(client, viewer, entityName, projectName);
    // If metadata loading throws, or the workspace is unloaded and we return
    // early below, this promise is never awaited. Swallow rejections on this
    // reference to avoid an unhandled promise rejection; the await in
    // loadNamedWorkspaceForUrl still throws on failure.
    permsPromise.catch(() => {});

    const legacyAndNwViews = await dispatch(
      ViewThunks.loadMetadataList(startAction)
    );

    const legacyViews = legacyAndNwViews
      .filter(
        v =>
          viewIsLegacyWorkspace(v.name) &&
          getNamedWorkspaceIdForView(v.name, v.user).relatedId === relatedId
      )
      .reduce((acc: Record<string, ViewTypes.View>, curr) => {
        acc[curr.cid] = curr;
        return acc;
      }, {});

    // loadMetadataList makes network calls, which could hang, and during which we could have
    // already started unloading views. Let's detect and handle that here by making sure
    // the workspaceID we're interested in still exists in redux.
    const state = getState();
    const workspace = state.workspaces[workspaceStateId] as
      | (typeof state)['workspaces'][string]
      | undefined;
    if (workspace == null) {
      return Promise.resolve();
    }

    // We are using legacy views as the source of truth now, so all legacy views are valid cids
    const validViewCids = Object.keys(legacyViews);
    for (const view of legacyAndNwViews) {
      if (viewIsNamedWorkspace(view) && !(view.cid in validViewCids)) {
        // make sure the relatedId matches so we have only views that should be visible.
        // the view type does filtering for other types, but within the sweeps views, you need to
        // also check relatedId
        try {
          const nwId = getNamedWorkspaceIdForView(view.name, view.user);
          if (nwId.relatedId === relatedId) {
            validViewCids.push(view.cid);
          }
        } catch (e) {
          // If it's a malformed view.name, let's just ignore it since it most likely was caused
          // by previous iterations and bugs
          continue;
        }
      }
    }
    await dispatch(newWorkspaceViews(workspaceStateId, validViewCids));

    const viewListSelector = makeViewListSelector(
      entityName,
      projectName,
      viewType
    );
    const viewList = viewListSelector(state);
    return dispatch(
      loadNamedWorkspaceForUrl(
        workspaceStateId,
        workspaceUrl,
        viewList.views,
        viewType,
        permsPromise
      )
    );
  };
}
